import React, { useState } from 'react'

import {
  pantryColors,
  pantryRadii,
  pantryShadows,
} from '../../../foundations/tokens'

export interface TooltipProps {
  label: React.ReactNode
  children: React.ReactNode
  width?: number | string
  // Extra styles for the hover wrapper (e.g. `maxWidth` so a truncating
  // child can shrink inside a flex row).
  style?: React.CSSProperties
  // Which side of the trigger the bubble opens on. Use 'top' when the trigger
  // sits at the bottom of an `overflow: hidden` container.
  placement?: 'bottom' | 'top'
  // Which trigger edge the bubble lines up with. 'end' anchors its right edge
  // to the trigger's, for triggers near the right of an `overflow: hidden`
  // container.
  align?: 'start' | 'end'
  // Sets the bubble's id and role="tooltip", so the trigger can point at it
  // with `aria-describedby`.
  id?: string
}

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  children,
  width = 230,
  style,
  placement = 'bottom',
  align = 'start',
  id,
}) => {
  // Tracked apart so leaving with the mouse keeps it open while focused.
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const open = hovered || focused

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Focus events bubble in React, so a focusable child opens the tooltip
      // for keyboard users too.
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
      <span
        id={id}
        role={id ? 'tooltip' : undefined}
        style={{
          position: 'absolute',
          ...(placement === 'top'
            ? { bottom: 'calc(100% + 8px)' }
            : { top: 'calc(100% + 8px)' }),
          ...(align === 'end' ? { right: 0 } : { left: 0 }),
          width,
          padding: '9px 12px',
          borderRadius: pantryRadii.md,
          background: pantryColors.gray[800],
          color: pantryColors.white,
          fontSize: 11.5,
          lineHeight: 1.6,
          boxShadow: pantryShadows.lg,
          opacity: open ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 0.18s',
          zIndex: 20,
        }}
      >
        {label}
      </span>
    </span>
  )
}
